"use client";
import dynamic from 'next/dynamic';
import React, { useState, Fragment, useEffect } from "react";
const Button = dynamic(() => import('@/components/ui/Button'));
import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuth } from "@/contexts/authContext";
import { supabase } from "@/supabase/client";
// import LauncherTeaserModal from "./launcher-teaser-modal";

let latestLauncherInfoCache = null;
let latestLauncherInfoPromise = null;
const launcherUserCache = new Map();
const launcherUserInflight = new Map();

// Function to get the latest launcher version and download URL
export const getLatestLauncherInfo = async () => {
  if (latestLauncherInfoCache) {
    return latestLauncherInfoCache;
  }

  if (latestLauncherInfoPromise) {
    return latestLauncherInfoPromise;
  }

  latestLauncherInfoPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('mxs_play_launcher')
        .select('download_url, version_number')
        .eq('is_active', true)
        .order('version_number', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error('Error fetching latest launcher:', error);
        return { downloadUrl: null, versionNumber: null };
      }

      const result = {
        downloadUrl: data?.download_url || null,
        versionNumber: data?.version_number || null
      };
      latestLauncherInfoCache = result;
      return result;
    } catch (error) {
      console.error('Error fetching latest launcher:', error);
      return { downloadUrl: null, versionNumber: null };
    } finally {
      latestLauncherInfoPromise = null;
    }
  })();

  return latestLauncherInfoPromise;
};

export const getLauncherUserStatus = async (uid, { forceRefresh = false } = {}) => {
  if (!uid) return null;

  if (!forceRefresh && launcherUserCache.has(uid)) {
    return launcherUserCache.get(uid);
  }

  if (!forceRefresh && launcherUserInflight.has(uid)) {
    return launcherUserInflight.get(uid);
  }

  const request = (async () => {
    try {
      const { data, error } = await supabase
        .from('mxs_play_launcher_user')
        .select('*')
        .eq('uid', uid)
        .maybeSingle();

      if (error) {
        console.error('Error checking launcher status:', error);
        return null;
      }

      const result = data ?? null;
      launcherUserCache.set(uid, result);
      return result;
    } catch (error) {
      console.error('Error checking launcher status:', error);
      return null;
    } finally {
      launcherUserInflight.delete(uid);
    }
  })();

  launcherUserInflight.set(uid, request);
  return request;
};

export const invalidateLauncherUserStatus = (uid) => {
  if (!uid) return;
  launcherUserCache.delete(uid);
  launcherUserInflight.delete(uid);
};

// Function to handle download with tracking
export const handleDownload = async (user, userId, ipAddress) => {
  const { downloadUrl, versionNumber } = await getLatestLauncherInfo();

  if (user && userId) {
    try {
      const existingUser = await getLauncherUserStatus(userId);
      const currentDateTime = new Date().toISOString();

      if (!existingUser) {
        const { error: insertError } = await supabase
          .from('mxs_play_launcher_user')
          .insert({
            uid: userId,
            downloaded_at: currentDateTime,
            status: 'downloaded',
            launcher_version: versionNumber || '1.0.0',
            public_ip: ipAddress
          });

        if (insertError) {
          console.error('Error creating launcher user entry:', insertError);
        }
      } else {
        const { error: updateError } = await supabase
          .from('mxs_play_launcher_user')
          .update({
            downloaded_at: currentDateTime,
            updated_at: currentDateTime,
            launcher_version: versionNumber || existingUser.launcher_version || '1.0.0',
            public_ip: ipAddress,
            status: 'downloaded'
          })
          .eq('uid', userId);

        if (updateError) {
          console.error('Error updating launcher user entry:', updateError);
        }
      }

      invalidateLauncherUserStatus(userId);
    } catch (error) {
      console.error('Error handling download tracking:', error);
    }
  }

  if (downloadUrl) {
    window.location.href = downloadUrl;
  } else {
    console.error('No download URL available');
  }
};

// Function to handle launch
export const handleLaunch = async (e) => {
  e.preventDefault();

  // Try to launch the launcher
  window.location.href = "mxsplay://launch";
};

const Launcher = ({ launcher }) => {
  const { isAuthenticated, isFetchingUser, user, ipAddress } = useAuth();
  const [launcherStatus, setLauncherStatus] = useState(null);
  // const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (launcher !== null && isAuthenticated && !isFetchingUser && user) {
      // Check if launcher status is "installed"
      if (launcher.status === 'installed') {
        setLauncherStatus(true);
      } else {
        setLauncherStatus(false);
      }
    } else {
      setLauncherStatus(false);
    }
  }, [launcher, isAuthenticated, isFetchingUser, user]);

  // NEW: Teaser functionality for development version (disabled for go-live)
  // const handleLauncherTeaser = (e) => {
  //   e.preventDefault();
  //   setIsModalOpen(true);
  // };

  return (
    <Fragment>
      {/* Show Launch if installed, otherwise Download */}
      <div style={{ display: "flex", gap: 12 }}>
        {launcherStatus ? (
          <Button
            className="shadow-sm focus:outline-none !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none btn-dark !bg-metaxseed-blue border-2 hover:jello-horizontal border-metaxseed-gray text-white text-xs lg:text-sm gothamMedium rounded-full px-2 py-1 sm:px-3 sm:py-2 flex items-center justify-center"
            onClick={(e) => handleLaunch(e)}
            // onClick={(e) => handleLauncherTeaser(e)}
            >
            <div className="flex items-center font-normal">
              <Icon icon="ion:rocket" style={{ fontSize: "24px" }} /> <span className="hidden lg:block ml-1"> Launch</span>
            </div>
          </Button>
        ) : (
          <Button
            className="shadow-sm focus:outline-none !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none btn-dark border-2 border-metaxseed-gray text-white text-xs lg:text-sm gothamMedium rounded-full px-2 py-1 sm:px-3 sm:py-2 flex items-center justify-center"
            onClick={() => handleDownload(user, user?.uid, ipAddress)}
            // onClick={(e) => handleLauncherTeaser(e)}
            >
            <div className="flex items-center font-normal">
              <Icon icon="line-md:download-loop" style={{ fontSize: "24px" }} /> <span className="hidden lg:block ml-1"> MXS Play</span>
            </div>
          </Button>
        )}
      </div>

      {/* Launcher Teaser Modal - disabled for go-live */}
      
      {/* <LauncherTeaserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      /> */}
     
    </Fragment>
  );
};

export default Launcher;
